import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { RootLayout } from '@/components/layout/RootLayout'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { MasterAuthGuard } from '@/components/layout/MasterAuthGuard'
import { MasterLayout } from '@/components/layout/MasterLayout'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'

// Lazy-loaded page modules
const LoginPage = lazy(() => import('@/pages/Login'))
const DashboardPage = lazy(() => import('@/pages/Dashboard'))
const AttendancePage = lazy(() => import('@/pages/Attendance'))
const SalesPage = lazy(() => import('@/pages/Sales'))
const SalesDashboardPage = lazy(() => import('@/pages/SalesDashboard'))
const SalesUploadPage = lazy(() => import('@/pages/SalesUpload'))
const SalesHistoryPage = lazy(() => import('@/pages/SalesHistory'))
const CrmPage = lazy(() => import('@/pages/CRM'))
const CrmProfessionalsPage = lazy(() => import('@/pages/CrmProfessionals'))
const CrmServicesPage = lazy(() => import('@/pages/CrmServices'))
const CrmBookingSettingsPage = lazy(() => import('@/pages/CrmBookingSettings'))
const CrmClientFilesPage = lazy(() => import('@/pages/CrmClientFiles'))
const CrmMigrationNewPage = lazy(() => import('@/pages/CrmMigrationNew'))
const CrmMigrationReviewPage = lazy(() => import('@/pages/CrmMigrationReview'))
const CrmProfilePage = lazy(() => import('@/pages/CrmProfile'))
const CrmAppointmentsPage = lazy(() => import('@/pages/CrmAppointments'))
const CrmVisitReportsPage = lazy(() => import('@/pages/CrmVisitReports'))
const CrmBlocksPage = lazy(() => import('@/pages/CrmBlocks'))
const CrmWaitlistPage = lazy(() => import('@/pages/CrmWaitlist'))
const CrmIntegrationsPage = lazy(() => import('@/pages/CrmIntegrations'))
const CrmDashboardPage = lazy(() => import('@/pages/CrmDashboard'))
const CrmTasksPage = lazy(() => import('@/pages/CrmTasks'))
const StockPage = lazy(() => import('@/pages/Stock'))
const StockDashboardPage = lazy(() => import('@/pages/StockDashboard'))
const StockRawMaterialsPage = lazy(() => import('@/pages/StockRawMaterials'))
const StockProductsPage = lazy(() => import('@/pages/StockProducts'))
const StockRecipesPage = lazy(() => import('@/pages/StockRecipes'))
const StockSalesPage = lazy(() => import('@/pages/StockSales'))
const StockPeriodsPage = lazy(() => import('@/pages/StockPeriods'))
const StockProfitabilityPage = lazy(() => import('@/pages/StockProfitability'))
const StockCostsPage = lazy(() => import('@/pages/StockCosts'))
const MasterLoginPage = lazy(() => import('@/pages/MasterLogin'))
const MasterDashboardPage = lazy(() => import('@/pages/MasterDashboard'))
const MasterClientCreatePage = lazy(() => import('@/pages/MasterClientCreate'))
const MasterClientDetailPage = lazy(() => import('@/pages/MasterClientDetail'))
const MasterMessagingSupportPage = lazy(() => import('@/pages/MasterMessagingSupport'))
const MasterKafiObservabilityPage = lazy(() => import('@/pages/MasterKafiObservability'))
const MasterKafiOperationsPage = lazy(() => import('@/pages/MasterKafiOperations'))
const KafiObservabilityPage = lazy(() => import('@/pages/KafiObservability'))
const QrDisplayPage = lazy(() => import('@/pages/QrDisplay'))
const EmployeeListPage = lazy(() => import('@/pages/EmployeeList'))
const RecordsPage = lazy(() => import('@/pages/Records'))
const AlertsPage = lazy(() => import('@/pages/Alerts'))
const ChangePasswordPage = lazy(() => import('@/pages/auth/ChangePassword'))
const ScanLandingPage = lazy(() => import('@/pages/ScanLanding'))
const PublicQrDisplayPage = lazy(() => import('@/pages/PublicQrDisplay'))
const PayrollClosePage = lazy(() => import('@/pages/PayrollClose'))
const BookingPage = lazy(() => import('@/pages/BookingPage'))
const BookingManagePage = lazy(() => import('@/pages/BookingManage'))
const PaymentReturnPage = lazy(() => import('@/pages/PaymentReturn'))
const SettingsPage = lazy(() => import('@/pages/Settings'))
const AnalyticsPage = lazy(() => import('@/pages/Analytics'))
const TreasuryPage = lazy(() => import('@/pages/Treasury'))
const BillingPage = lazy(() => import('@/pages/Billing'))
const BillingIssuersPage = lazy(() => import('@/pages/BillingIssuers'))
const FinanceDashboardPage = lazy(() => import('@/pages/FinanceDashboard'))
const FinanceCostsPage = lazy(() => import('@/pages/FinanceCosts'))
const FinanceReceivablesPage = lazy(() => import('@/pages/FinanceReceivables'))
const FinanceQuotesPage = lazy(() => import('@/pages/FinanceQuotes'))
const FinanceProfitabilityPage = lazy(() => import('@/pages/FinanceProfitability'))
const FinanceAnalyticsPage = lazy(() => import('@/pages/FinanceAnalytics'))
const FinanceCardsPage = lazy(() => import('@/pages/FinanceCards'))
const FinanceCategoriesPage = lazy(() => import('@/pages/FinanceCategories'))
const FinanceGoalsPage = lazy(() => import('@/pages/FinanceGoals'))
const FinanceBudgetsPage = lazy(() => import('@/pages/FinanceBudgets'))
const HolidaysPage = lazy(() => import('@/pages/Holidays'))
const DocumentationPage = lazy(() => import('@/pages/Documentation'))
const KafiBotPage = lazy(() => import('@/pages/KafiBot'))

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="space-y-4 w-full max-w-2xl">
        <div className="skeleton h-8 w-48" />
        <div className="skeleton h-4 w-full" />
        <div className="skeleton h-4 w-3/4" />
      </div>
    </div>
  )
}

/** Ruta por defecto según permisos: usuarios con :own van a CRM, otros al Dashboard. */
function DefaultRoute() {
  const permissions = useAuthStore((s) => s.admin?.permissions) || []
  const isScoped = ['crm:appointments:own', 'crm:files:own', 'crm:reports:own'].some((p) => permissions.includes(p))
  return <Navigate to={isScoped ? '/crm/files' : '/dashboard'} replace />
}

function LazyPage({ Component }: { Component: React.LazyExoticComponent<React.ComponentType> }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Component />
      </Suspense>
    </ErrorBoundary>
  )
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public routes
      {
        path: '/login',
        element: <LazyPage Component={LoginPage} />,
      },
      {
        path: '/scan',
        element: <LazyPage Component={ScanLandingPage} />,
      },
      {
        path: '/display',
        element: <LazyPage Component={PublicQrDisplayPage} />,
      },
      {
        path: '/book/:tenantSlug',
        element: <LazyPage Component={BookingPage} />,
      },
      {
        path: '/book/:tenantSlug/manage/:token',
        element: <LazyPage Component={BookingManagePage} />,
      },
      {
        path: '/book/:tenantSlug/:professionalSlug',
        element: <LazyPage Component={BookingPage} />,
      },
      {
        path: '/book/:tenantSlug/payment/success',
        element: <LazyPage Component={PaymentReturnPage} />,
      },
      {
        path: '/book/:tenantSlug/payment/failure',
        element: <LazyPage Component={PaymentReturnPage} />,
      },
      {
        path: '/book/:tenantSlug/payment/pending',
        element: <LazyPage Component={PaymentReturnPage} />,
      },
      // Authenticated routes
      {
        element: <AuthLayout />,
        children: [
          { index: true, element: <DefaultRoute /> },
          { path: 'dashboard', element: <LazyPage Component={DashboardPage} /> },
          { path: 'analytics', element: <LazyPage Component={AnalyticsPage} /> },
          { path: 'change-password', element: <LazyPage Component={ChangePasswordPage} /> },
          { path: 'settings', element: <LazyPage Component={SettingsPage} /> },
          {
            path: 'attendance',
            element: <LazyPage Component={AttendancePage} />,
            children: [
              { index: true, element: <Navigate to="/attendance/qr" replace /> },
              { path: 'qr', element: <LazyPage Component={QrDisplayPage} /> },
              { path: 'employees', element: <LazyPage Component={EmployeeListPage} /> },
              { path: 'records', element: <LazyPage Component={RecordsPage} /> },
              { path: 'alerts', element: <LazyPage Component={AlertsPage} /> },
              { path: 'holidays', element: <LazyPage Component={HolidaysPage} /> },
              { path: 'payroll', element: <LazyPage Component={PayrollClosePage} /> },
            ],
          },
          {
            path: 'sales',
            element: <LazyPage Component={SalesPage} />,
            children: [
              { index: true, element: <LazyPage Component={SalesDashboardPage} /> },
              { path: 'upload', element: <LazyPage Component={SalesUploadPage} /> },
              { path: 'history', element: <LazyPage Component={SalesHistoryPage} /> },
            ],
          },
          {
            path: 'crm',
            element: <LazyPage Component={CrmPage} />,
            children: [
              { index: true, element: <LazyPage Component={CrmProfessionalsPage} /> },
              { path: 'services', element: <LazyPage Component={CrmServicesPage} /> },
              { path: 'files', element: <LazyPage Component={CrmClientFilesPage} /> },
              { path: 'migrations/new', element: <LazyPage Component={CrmMigrationNewPage} /> },
              { path: 'migrations/:jobId/review', element: <LazyPage Component={CrmMigrationReviewPage} /> },
              { path: 'profile', element: <LazyPage Component={CrmProfilePage} /> },
              { path: 'appointments', element: <LazyPage Component={CrmAppointmentsPage} /> },
              { path: 'reports', element: <LazyPage Component={CrmVisitReportsPage} /> },
              { path: 'reminders', element: <LazyPage Component={CrmBookingSettingsPage} /> },
              { path: 'blocks', element: <LazyPage Component={CrmBlocksPage} /> },
              { path: 'waitlist', element: <LazyPage Component={CrmWaitlistPage} /> },
              { path: 'integrations', element: <LazyPage Component={CrmIntegrationsPage} /> },
              { path: 'dashboard', element: <LazyPage Component={CrmDashboardPage} /> },
              { path: 'tasks', element: <LazyPage Component={CrmTasksPage} /> },
            ],
          },
          { path: 'treasury', element: <LazyPage Component={TreasuryPage} /> },
          { path: 'billing', element: <LazyPage Component={BillingPage} /> },
          { path: 'billing/issuers', element: <LazyPage Component={BillingIssuersPage} /> },
          { path: 'finance', element: <LazyPage Component={FinanceDashboardPage} /> },
          { path: 'finance/costs', element: <LazyPage Component={FinanceCostsPage} /> },
          { path: 'finance/receivables', element: <LazyPage Component={FinanceReceivablesPage} /> },
          { path: 'finance/quotes', element: <LazyPage Component={FinanceQuotesPage} /> },
          { path: 'finance/profitability', element: <LazyPage Component={FinanceProfitabilityPage} /> },
          { path: 'finance/analytics', element: <LazyPage Component={FinanceAnalyticsPage} /> },
          { path: 'finance/cards', element: <LazyPage Component={FinanceCardsPage} /> },
          { path: 'documentation', element: <LazyPage Component={DocumentationPage} /> },
          { path: 'kafi-bot', element: <LazyPage Component={KafiBotPage} /> },
          { path: 'kafi/overview', element: <ProtectedRoute permission="messaging:analytics:view"><LazyPage Component={KafiObservabilityPage} /></ProtectedRoute> },
          { path: 'finance/payables', element: <Navigate to="/finance/costs" replace /> },
          { path: 'finance/categories', element: <LazyPage Component={FinanceCategoriesPage} /> },
          { path: 'finance/goals', element: <LazyPage Component={FinanceGoalsPage} /> },
          { path: 'finance/budgets', element: <LazyPage Component={FinanceBudgetsPage} /> },
          {
            path: 'stock',
            element: <LazyPage Component={StockPage} />,
            children: [
              { index: true, element: <LazyPage Component={StockDashboardPage} /> },
              { path: 'materials', element: <LazyPage Component={StockRawMaterialsPage} /> },
              { path: 'products', element: <LazyPage Component={StockProductsPage} /> },
              { path: 'recipes', element: <LazyPage Component={StockRecipesPage} /> },
              { path: 'sales', element: <LazyPage Component={StockSalesPage} /> },
              { path: 'profitability', element: <LazyPage Component={StockProfitabilityPage} /> },
              { path: 'costs', element: <LazyPage Component={StockCostsPage} /> },
              { path: 'periods', element: <LazyPage Component={StockPeriodsPage} /> },
            ],
          },
          // Catch-all for authenticated users (unknown routes → ruta por defecto del rol)
          { path: '*', element: <DefaultRoute /> },
        ],
      },
      // Master admin routes (separate auth, separate layout)
      {
        path: 'master',
        children: [
          { path: 'login', element: <LazyPage Component={MasterLoginPage} /> },
          {
            element: <MasterAuthGuard />,
            children: [
              {
                element: <MasterLayout />,
                children: [
                  { path: 'dashboard', element: <LazyPage Component={MasterDashboardPage} /> },
                  { path: 'clients/create', element: <LazyPage Component={MasterClientCreatePage} /> },
                  { path: 'clients/:id', element: <LazyPage Component={MasterClientDetailPage} /> },
                  { path: 'messaging', element: <LazyPage Component={MasterMessagingSupportPage} /> },
                  { path: 'kafi', element: <LazyPage Component={MasterKafiObservabilityPage} /> },
                  { path: 'kafi/operations', element: <LazyPage Component={MasterKafiOperationsPage} /> },
                  { index: true, element: <Navigate to="/master/dashboard" replace /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
])
